/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useSystemMiscColumns.tsx
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for defining System Misc DataGrid columns.
────────────────────────────────────────────────────────────────*/

import { useMemo, useCallback, useState, useEffect } from 'react';
import { useI18n } from '../../../hooks/useI18n';
import { DataGridColumn } from '../../../hooks/useDataGrid';
import { SystemMiscConfig, STATUS_BADGES } from '../types';

// Component to fetch real-time telephony status from monitoring-api
const TelephonyStatusCell = ({ fallbackStatus }: { fallbackStatus: string }) => {
  const { t } = useI18n();
  const [status, setStatus] = useState(fallbackStatus);
  const [version, setVersion] = useState<string | null>(null);

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const protocol = window.location.protocol;
        const host = window.location.host;
        const response = await fetch(`${protocol}//${host}/monitoring-api/api/system/stats`);
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data?.services?.freeswitch) {
            const fs = data.data.services.freeswitch;
            // Map monitoring-api status to our status labels
            const apiStatus = fs.status || 'unknown';
            const mappedStatus = apiStatus === 'active' ? 'online' : apiStatus === 'inactive' ? 'offline' : apiStatus;
            setStatus(mappedStatus);
            setVersion(fs.version && fs.version !== 'N/A' ? fs.version : null);
          }
        }
      } catch {
        // Use fallback status
      }
    };
    fetchStatus();
  }, []);

  const badge = STATUS_BADGES[status] ?? STATUS_BADGES.unknown;

  return (
    <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs ${badge.className}`}>
      {t(badge.labelKey)}
      {version && <span className="ml-1 opacity-75">({version})</span>}
    </span>
  );
};

export function useSystemMiscColumns() {
  const { t } = useI18n();

  const renderStatusBadge = useCallback(
    (status: string) => {
      const badge = STATUS_BADGES[status] ?? STATUS_BADGES.unknown;
      return (
        <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs ${badge.className}`}>
          {t(badge.labelKey)}
        </span>
      );
    },
    [t],
  );

  const availableColumns = useMemo<DataGridColumn[]>(
    () => [
      { key: 'name', label: t('systemMisc.fields.name'), visible: true, sortable: true },
      {
        key: 'host',
        label: t('systemMisc.host'),
        visible: true,
        sortable: true,
        render: (_: unknown, config: SystemMiscConfig) => config.server?.host ?? t('systemMisc.unknown'),
      },
      {
        key: 'status',
        label: t('systemMisc.serverStatus'),
        visible: true,
        sortable: true,
        render: (_: unknown, config: SystemMiscConfig) => renderStatusBadge(config.server?.status ?? 'unknown'),
      },
      {
        key: 'telephonyStatus',
        label: t('systemMisc.telephonyStatus'),
        visible: true,
        sortable: true,
        render: (_: unknown, config: SystemMiscConfig) => (
          <TelephonyStatusCell fallbackStatus={config.server?.telephonyStatus ?? 'unknown'} />
        ),
      },
      {
        key: 'lastSeen',
        label: t('systemMisc.lastSeen'),
        visible: true,
        sortable: true,
        render: (_: unknown, config: SystemMiscConfig) =>
          config.server?.lastSeen ? new Date(config.server.lastSeen).toLocaleString() : t('systemMisc.never'),
      },
      {
        key: 'monitoringEnabled',
        label: t('systemMisc.monitoring'),
        visible: true,
        sortable: true,
        render: (_: unknown, config: SystemMiscConfig) =>
          config.server?.monitoringEnabled
            ? t('systemMisc.monitoringEnabledLabel.on')
            : t('systemMisc.monitoringEnabledLabel.off'),
      },
      {
        key: 'actions',
        label: t('common.actions'),
        visible: true,
        sortable: false,
      },
    ],
    [t, renderStatusBadge],
  );

  return availableColumns;
}
